import { ReactNode, useState } from "react";

import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    Avatar,
    Box,
    Flex,
    MenuContent,
    MenuPositioner,
    MenuRoot,
    MenuTrigger,
    Text
} from "@chakra-ui/react";

import { truncateName } from "@utils/utils.ts";

interface ProfileMenuProps {
    displayName?: string;
    children: ReactNode;
    onMenuToggle?: (isOpen: boolean) => void;
    profileImageSrc?: string;
    hasMenuItems?: boolean;
}

const ProfileMenu = ({
    displayName = "User",
    children,
    onMenuToggle,
    profileImageSrc,
    hasMenuItems = true
}: ProfileMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleOpenChange = (details: { open: boolean }) => {
        setIsOpen(details.open);
        onMenuToggle?.(details.open);
    };

    if (!hasMenuItems) {
        return (
            <Flex alignItems="center" gap={2}>
                {profileImageSrc && (
                    <Avatar.Root size="xs">
                        <Avatar.Fallback name={displayName} />
                        <Avatar.Image src={profileImageSrc} />
                    </Avatar.Root>
                )}
                <Text
                    fontWeight={600}
                    color="base.800"
                    _dark={{ color: "base.200" }}
                >
                    {truncateName(displayName)}
                </Text>
            </Flex>
        );
    }

    return (
        <MenuRoot
            onOpenChange={handleOpenChange}
            navigate={({ value, node }) => {
                console.log(value, node);
            }}
        >
            <MenuTrigger
                asChild
                cursor="pointer"
                title={displayName}
                textDecor="none"
                _hover={{
                    textDecor: "none"
                }}
            >
                <Flex alignItems="center" gap={2}>
                    {profileImageSrc && (
                        <Avatar.Root size="xs">
                            <Avatar.Fallback name={displayName} />
                            <Avatar.Image src={profileImageSrc} />
                        </Avatar.Root>
                    )}
                    <Text
                        fontWeight={600}
                        display="flex"
                        gap={2}
                        color="base.800"
                        _dark={{ color: "base.200" }}
                    >
                        {truncateName(displayName)}
                        {hasMenuItems && (
                            <Box
                                as="span"
                                transition="transform 0.2s ease"
                                transform={isOpen ? "rotate(180deg)" : "rotate(0)"}
                                transformOrigin="center"
                                color="base.600"
                                _dark={{ color: "base.400" }}
                            >
                                <FontAwesomeIcon
                                    icon={faChevronDown}
                                    fontSize={14}
                                    data-testid="chevronDownImg"
                                />
                            </Box>
                        )}
                    </Text>
                </Flex>
            </MenuTrigger>

            <MenuPositioner>
                <MenuContent
                    borderRadius="1rem"
                    boxShadow="0px 4px 20px -2px rgba(50, 50, 71, 0.08), 0px 0px 1px 0px rgba(12, 26, 75, 0.10)"
                    borderColor="gray.200"
                    padding={2}
                    bg="white"
                    _dark={{
                        bg: "base.800",
                        borderColor: "base.600",
                        boxShadow: "0px 4px 20px -2px rgba(0, 0, 0, 0.3), 0px 0px 1px 0px rgba(0, 0, 0, 0.5)"
                    }}
                >
                    {children}
                </MenuContent>
            </MenuPositioner>
        </MenuRoot>
    );
};

export default ProfileMenu;
